<template>
  <ConfirmDialog
    class="add-workspace"
    :open="open"
    @cancel="closeModal"
    @confirm="onCreateInvite"
  >
    <template #title>
      <div class="add-workspace__title">Create a new workspace</div>
    </template>
    <template #body>
      <div class="add-workspace__body">
        <div class="add-workspace__description">
          Enter the email of the person who will own this new workspace.
        </div>

        <TextInput
          v-model="email"
          auto-focus
          placeholder="Enter the workspace owner's email"
          required
          :error="validationError"
        />
        <div
          v-if="!flagsStore.featureFlags.WORKSPACE_PRICING"
          class="add-workspace__network"
        >
          <CheckBox
            v-model="neuralNetworks"
            name="cover-team-usage-checkbox"
            size="lg"
          >
            <template #label>Cover this team's usage of neural networks</template>
          </CheckBox>
          <InfoIcon>
            <template #tooltip-content>
              <p>
                If active, your (partner) workspace will be billed for the invited workspace's usage
                of neural network model training and inference.
              </p>
              <p>
                Ensure you have included these costs in your relationship, or have the means to bill
                for them.
              </p>
              <p>
                If un-checked, the workspace will not be able to use neural networks until they
                enter their billing details.
              </p>
            </template>
          </InfoIcon>
        </div>
      </div>
    </template>
    <template #confirm-text>Send</template>
  </ConfirmDialog>
</template>

<script lang="ts" setup>
import isEmail from 'validator/lib/isEmail'
import isEmpty from 'validator/lib/isEmpty'
import { ref } from 'vue'

import { createClientTeamInvitation } from '@/backend/darwin/createClientTeamInvitation'
import { useToast } from '@/uiKit/Toast/useToast'
import { errorMessages } from '@/backend/error'
import { useFeatureFlagsStore } from '@/pinia/useFeatureFlagsStore'

import CheckBox from '@/uiKit/Checkbox/CheckBox.vue'
import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import TextInput from '@/uiKit/TextInput/TextInput.vue'
import InfoIcon from '@/uiKit/InfoIcon/InfoIcon.vue'

const toast = useToast()

const flagsStore = useFeatureFlagsStore()

const props = defineProps<{
  open: boolean
  id: number
  slug: string
}>()

const emit = defineEmits<{
  (e: 'update:open', opened: boolean): void
}>()

const email = ref('')
const neuralNetworks = ref(true)

const validationError = ref<string | undefined>()
const validationMessage = (email: string): string | undefined => {
  if (isEmpty(email)) {
    return 'Please enter an email address'
  }

  if (!isEmail(email)) {
    return 'Please enter a valid email address'
  }
}

const closeModal = (): void => {
  email.value = ''
  neuralNetworks.value = true
  validationError.value = undefined
  emit('update:open', false)
}

const onCreateInvite = async (): Promise<void> => {
  const validationErrors = validationMessage(email.value)
  validationError.value = validationErrors

  if (validationErrors) {
    return
  }

  const response = await createClientTeamInvitation({
    email: email.value,
    neuralNetworks: neuralNetworks.value,
    teamId: props.id,
  })

  if ('error' in response) {
    toast.warning({
      meta: {
        title:
          response.error.status === 422
            ? errorMessages.PARTNER_CREATE_INVITATION[422]
            : response.error.message.toString(),
      },
    })
    return
  }

  toast.success({ meta: { title: `Invite sent successfully to ${email.value}` } })

  closeModal()
}
</script>

<style lang="scss" scoped>
.add-workspace {
  &__title {
    @include typographyBoldBody500;
  }

  &__description {
    @include typographyRegularBody300;
    color: var(--color-text-subtlest);
  }

  &__body {
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__network {
    display: flex;
    justify-content: space-between;
  }
}
</style>
